// biome-ignore lint/correctness/noUnusedImports: useCallback is used in switchView below
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import SegmentedControl from '@/components/molecules/SegmentedControl';
import { useAuth } from '@/contexts/auth';
import { DashboardView } from '@/domain/auth';
import { useSaveDashboardView } from '@/domain/users';
import {
    FISH_GROUPS_VIEW_FLAG,
    locationDashboardPath,
    usePreferredDashboardView,
} from '@/hooks/useLocationDashboardUrl';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';

import styles from './DashboardViewSwitcher.module.scss';

export function DashboardViewSwitcher() {
    const { user } = useAuth();
    const savedDefault = usePreferredDashboardView();
    // A stored board default shows as Biological without the flag; picking Biological must still
    // replace it, so saving compares against what is stored, not against the fallback.
    const storedDefault = user?.profile?.defaultLocationDashboard ?? DashboardView.biological;
    const { enabled: fishGroupsEnabled } = usePostHogFeatureFlag(FISH_GROUPS_VIEW_FLAG);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { clientId, locationId } = useParams<{ clientId: string; locationId: string }>();
    const saveDashboardView = useSaveDashboardView();

    const basePath = `/c/${clientId}/location/${locationId}`;

    const switchView = useCallback(
        (view: DashboardView) => {
            // Save preference if it changed
            if (view !== storedDefault) {
                saveDashboardView(view);
            }

            // Always navigate, even if preference didn't change
            navigate(locationDashboardPath(basePath, view));
        },
        [storedDefault, basePath, navigate, saveDashboardView]
    );

    return (
        <div className={styles.container}>
            <span className={styles.label}>{t('operationalDashboard.defaultView')}:</span>
            <SegmentedControl<DashboardView>
                aria-label={t('operationalDashboard.defaultView')}
                value={savedDefault}
                onChange={(view) => switchView(view)}
                options={[
                    {
                        value: DashboardView.biological,
                        label: t('sidebarItems.location.biologicalDashboard'),
                    },
                    {
                        value: DashboardView.operational,
                        label: t('sidebarItems.location.operationalDashboard'),
                    },
                    ...(fishGroupsEnabled
                        ? [
                              {
                                  value: DashboardView.fishGroups,
                                  label: t('sidebarItems.location.fishGroups'),
                              },
                          ]
                        : []),
                ]}
            />
        </div>
    );
}
